import classNames from 'classnames'
import React from 'react'
import { Bar, Cell, ComposedChart, Line, Rectangle, RectangleProps, Tooltip, YAxis } from 'recharts'

import { ColorVariant } from '../../../components'
import {
  BAR_GAP,
  BAR_WIDTH,
  BAR_WIDTH_NARROW,
  CHART_HEIGHT,
  CHART_MARGINS,
  CURSOR_STYLE,
  EXTRA_WIDTH,
  LABELS_MARGIN,
  LABELS_WIDTH,
  TICK_STYLE,
  TOOLTIP_WRAPPER_STYLE,
  TRANSPARENT_CURSOR,
} from '../constants'
import { EarningReportReaction, EarningValueProperty, EarningsDataType, EarningsEstimateType } from '../types'
import { getColor, tickFormatter } from '../utils'
import { PerformanceTooltip } from './PerformanceTooltip'
import { RedGreenDot } from './RedGreenDot'

interface CustomCursorProps extends Partial<Omit<RectangleProps, 'points'>> {
  barWithGapWidth: number
  isActive: boolean
  points?: [{ x: number }]
}

function CustomCursor({ barWithGapWidth, points, ...props }: CustomCursorProps) {
  const x = points![0].x - barWithGapWidth / 2
  return (
    <Rectangle {...props} x={x} width={barWithGapWidth} {...(props.isActive ? CURSOR_STYLE : TRANSPARENT_CURSOR)} />
  )
}

export type Props = {
  annualData: EarningsDataType[]
  estimateType: EarningsEstimateType
  isCompact: boolean
  reportReactions: EarningReportReaction[]
}

export type ChartYearData = {
  year: string
  isEstimate: boolean
  reportReaction: EarningReportReaction | undefined
} & Record<EarningsDataType['id'], number | null>

const CHART_GRID_COMMON_PROPS = 'absolute right-8 border-t border-dashed border-secondary'

export function EarningsChart({ annualData, estimateType, isCompact, reportReactions }: Props) {
  const [isActive, setIsActive] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [reverseTooltipDirection, setReverseTooltipDirection] = React.useState(true)
  const columnsCount = annualData[0].values.length
  const barWidth = isCompact ? BAR_WIDTH_NARROW : BAR_WIDTH
  const { estimateColumnsCount, hasBarChart, hasLineChart } = React.useMemo(
    () => ({
      estimateColumnsCount: annualData[0].values.filter(({ isEstimate }) => isEstimate).length,
      hasLineChart: annualData.some(({ id, isSelected }) => id === EarningValueProperty.Surprise && isSelected),
      hasBarChart: annualData.some(({ id, isSelected }) => id !== EarningValueProperty.Surprise && isSelected),
    }),
    [annualData]
  )
  const chartData = React.useMemo(() => {
    const data: Record<string, ChartYearData> = {}
    annualData.forEach(({ id, values }) => {
      values.forEach(({ value, year, isEstimate }) => {
        const fiscalPeriod = `20${year.substring(4, 6)}${year.substring(0, 2)}`
        const reportReaction = reportReactions.find((reaction) => reaction.fiscalPeriod === fiscalPeriod)
        data[year] = { ...data[year], year, isEstimate, [id]: value, reportReaction }
      })
    })
    return Object.values(data)
  }, [annualData, reportReactions])

  const chartWidth = columnsCount * barWidth + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH + LABELS_WIDTH * 1.5
  const estimateAreaWidth = estimateColumnsCount * barWidth + estimateColumnsCount * BAR_GAP
  const barLessChartYMargin = barWidth / 2 + BAR_GAP / 2

  const chartGridCommonLeftProps = {
    'left-8': hasLineChart,
    'left-0': !hasLineChart,
  }

  return (
    <div className="flex w-full justify-end">
      <div className="relative flex justify-end" style={{ width: hasLineChart ? chartWidth : '100%' }}>
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-0', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-1/4', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-1/2', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'top-3/4', chartGridCommonLeftProps)} />
        <div className={classNames(CHART_GRID_COMMON_PROPS, 'bottom-0', chartGridCommonLeftProps)} />
        <div
          className="pointer-events-none absolute bottom-0 right-0 z-10 h-full bg-stripes text-gray-50 mix-blend-multiply dark:text-gray-100"
          style={{ width: estimateAreaWidth, right: LABELS_WIDTH }}
        />

        <div
          /**
           * Fix a problem with chart tooltip not hiding correctly on mouse leave
           * See: https://github.com/recharts/recharts/issues/2726
           */
          onMouseEnter={() => {
            setIsActive(true)
          }}
          onMouseLeave={() => {
            setIsActive(false)
          }}
        >
          <ComposedChart
            width={chartWidth}
            height={CHART_HEIGHT}
            data={chartData}
            margin={
              hasBarChart ? CHART_MARGINS : { ...CHART_MARGINS, left: barLessChartYMargin, right: barLessChartYMargin }
            }
            stackOffset="sign" // because we can have negative values
            onMouseMove={(props, mouseEvent) => {
              if (typeof props.activeTooltipIndex === 'number' && props.activeTooltipIndex !== activeIndex) {
                setActiveIndex(props.activeTooltipIndex)
              }
              setReverseTooltipDirection(mouseEvent.clientX > window.innerWidth / 2)
            }}
            onMouseLeave={() => {
              setActiveIndex(-1)
            }}
          >
            <YAxis
              className="font-sans text-muted-2"
              yAxisId="left"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={tickFormatter}
              tickMargin={hasBarChart ? 0 : barLessChartYMargin}
              width={LABELS_WIDTH * 1.5}
              interval="preserveStartEnd"
            />
            <YAxis
              className="font-sans text-muted-2"
              yAxisId="right"
              orientation="right"
              tick={TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={tickFormatter}
              tickMargin={LABELS_MARGIN}
              width={LABELS_WIDTH}
              interval="preserveStartEnd"
            />
            <Tooltip
              wrapperStyle={TOOLTIP_WRAPPER_STYLE}
              position={{ y: 0 }}
              allowEscapeViewBox={{ x: true }}
              reverseDirection={{ x: reverseTooltipDirection }}
              offset={40}
              isAnimationActive={false}
              cursor={<CustomCursor isActive={isActive} barWithGapWidth={barWidth + BAR_GAP} />}
              content={() => (
                <PerformanceTooltip data={chartData[activeIndex]} estimateType={estimateType} isActive={isActive} />
              )}
            />
            {annualData.map(({ id, label, isSelected, color }) => {
              if (!isSelected) return null

              if (id !== EarningValueProperty.Surprise) {
                return (
                  <Bar
                    key={id}
                    name={label}
                    dataKey={id}
                    fill={getColor(color)}
                    yAxisId="right"
                    isAnimationActive={false}
                  >
                    {chartData.map((row, index) => (
                      <Cell
                        key={index}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - number[] should also be allowed type for radius, but it's not
                        radius={[2, 2, 0, 0]}
                        fill={getColor(
                          [
                            EarningValueProperty.EpsEstimate,
                            EarningValueProperty.EpsReportedEstimate,
                            EarningValueProperty.SalesEstimate,
                          ].includes(id)
                            ? ColorVariant.gray
                            : color
                        )}
                      />
                    ))}
                  </Bar>
                )
              }

              return (
                <Line
                  key={id}
                  name={label}
                  dataKey={id}
                  yAxisId="left"
                  type="linear"
                  dot={<RedGreenDot />}
                  strokeDasharray="3 3"
                  activeDot={<RedGreenDot isActive onMouseEnter={() => {}} onMouseLeave={() => {}} />}
                  stroke={getColor(color)}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )
            })}
          </ComposedChart>
        </div>
      </div>
    </div>
  )
}
